import type { DeepReadonly } from 'vue';
import type { DrillData } from '@/components/forms/form_builder/FormFieldConfigHelper';
import { isAnswered } from '@/models/fieldAnswer';
import { DateMode, type FormField, type FormFieldChoice, type FormFieldType } from '@/models/form';
import { OTHER_CHOICE_CODE } from '@/models/entry';
import { formatDate, formatDateTime, formatTime } from '@/utils/dateDisplay';
import { readRangeAnswer } from './numberRangeAnswer';

/**
 * The text view mode draws for an answer (ENG-9764), in place of the read-only control edit mode draws, so the answer
 * can be selected and copied. Whether there is an answer at all is `isAnswered` (ADR 0009), so the row says "Not
 * answered" on exactly the fields Save, the ring and the pipeline count as unanswered. A date goes through the
 * `dateDisplay.ts` presets, never through the format the form configures for its picker (docs/memory/dates.md).
 *
 * A file, a signature and a matrix draw their own read-only content, and a title, a section and the rows the entry adds
 * for itself ask nothing, so none of them has a row here.
 */

type Text = (field: FormField) => string;

const numberText = (value: number | null | undefined) => `${value}`;

/** The picked choice's label, or for Other the text typed into it, which is what Save sends. */
const choiceLabels: Text = field => {
  if (field.options.maximumChoicesCount == 1) {
    const picked = field.choiceValue as FormFieldChoice | undefined;
    if (!picked?.value && field.options.allowOther && field.otherChoiceValue?.value === OTHER_CHOICE_CODE) {
      return field.otherChoiceValue.otherValue ?? '';
    }
    return picked?.label ?? '';
  }
  const labels = (field.options.choices ?? []).filter(choice => choice.checked).map(choice => choice.label);
  if (field.options.allowOther && field.otherChoiceValue?.checked && field.otherChoiceValue.otherValue) {
    labels.push(field.otherChoiceValue.otherValue);
  }
  return labels.join(', ');
};

const dropdownLabels: Text = field => {
  const picked = field.choiceValue;
  const list = Array.isArray(picked) ? picked : picked ? [picked] : [];
  return list.map(choice => choice.label).join(', ');
};

/** A date the loader could not read stays as the text it was saved as, which is what `toWire` sends back. */
const dateText: Text = field => {
  if (!field.dateValue) {
    return field.value ?? '';
  }
  switch (field.options.mode) {
    case DateMode.DATE:
      return formatDate(field.dateValue);
    case DateMode.TIME:
      return formatTime(field.dateValue);
    default:
      return formatDateTime(field.dateValue);
  }
};

/** "a – b"; an older answer holding one end reads as that end, and one the pattern can't read as it was saved. */
const rangeText = (field: FormField) => {
  const { from, to } = readRangeAnswer(field.value);
  if (from === undefined) {
    return field.value ?? '';
  }
  return to === undefined ? `${from}` : `${from} – ${to}`;
};

/** An inequality the reader picked, then the number and its unit: "< 5 mg". Clear leaves an empty choice. */
const unitText: Text = field => {
  const inequality = (field.choiceValue as FormFieldChoice | undefined)?.value ?
    (field.choiceValue as FormFieldChoice).label :
    undefined;
  return [inequality, numberText(field.numberValue), field.value || undefined].filter(Boolean).join(' ');
};

/**
 * The coordinates as the browser gave them, then its accuracy, a radius in metres, rounded to whole ones. A stored
 * answer the loader could not read stays as the text it was saved as, which is what `toWire` sends back.
 */
const locationText: Text = field => {
  if (!field.locationValue) {
    return field.value ?? '';
  }
  const { latitude, longitude, accuracy } = field.locationValue.coords;
  return `${latitude}, ${longitude} (±${Math.round(accuracy)} m)`;
};

const drilldownText: Text = field => ((field.drillValue as DrillData[] | undefined) ?? [])
  .map(level => level.label)
  .join(' › ');

/*
 * Keyed by the enum's string values, as `fieldAnswer.ts`'s table is, so it builds in any load order, and `Record` fails
 * `tsc` until a new type says whether it draws as text.
 */
const ANSWER_TEXT: Record<FormFieldType, Text | null> = {
  Text: field => field.value ?? '',
  Number: field => field.options.range ? rangeText(field) : numberText(field.numberValue),
  Slider: field => numberText(field.numberValue),
  Choice: choiceLabels,
  Dropdown: dropdownLabels,
  Date: dateText,
  Location: locationText,
  Drilldown: drilldownText,
  Unit: unitText,
  Barcode: field => field.value ?? '',
  File: null,
  Signature: null,
  Matrix: null,
  Title: null,
  Section: null,
  Branding: null,
  SubmitButton: null
};

/** Whether view mode draws this field's answer as text, rather than mounting its item read-only. */
export const answersAsText = (field: FormField): boolean => ANSWER_TEXT[field.type] !== null;

/** The answer as view mode draws it, or null when the field is not answered or draws its own content. */
export const answerText = (field: FormField): string | null => {
  const text = ANSWER_TEXT[field.type];
  return text && isAnswered(field) ? text(field) : null;
};

/** A note the date item draws under its picker, and view mode under the date's text: the two must not drift apart. */
export type DateNote = { kind: 'shifted' } | { kind: 'recordedIn'; timezone: string };

/** That the date is shifted for security, and the zone an absolute date was recorded in, where either applies. */
export const dateNotes = (field: DeepReadonly<FormField>): DateNote[] => {
  const notes: DateNote[] = [];
  if (field.options.dateShifting && field.dateValue) {
    notes.push({ kind: 'shifted' });
  }
  if (field.options.absoluteDate && field.timezoneRecorded) {
    notes.push({ kind: 'recordedIn', timezone: field.timezoneRecorded.timezone });
  }
  return notes;
};
